import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Calendar, List } from 'lucide-react'
import { listAppointments } from '../../api/appointments'
import { Button, ButtonLink } from '../../components/ui/Button'
import { Card, ErrorBox } from '../../components/ui/Card'
import { useFetch } from '../../hooks/useFetch'
import { thClass, tdClass, tbodyHoverClass, skeletonClass, filterInputClass } from '@/lib/tableStyles'
import type { AppointmentListItem, AppointmentStatus, AppointmentType } from '../../types/appointments'
import { STATUS_COLORS, STATUS_LABELS, TYPE_COLORS, TYPE_LABELS } from '../../types/appointments'
import AppointmentCalendar from './AppointmentCalendar'

function todayISO(): string { return new Date().toISOString().slice(0, 10) }
function plusDaysISO(days: number): string {
  const d = new Date(); d.setDate(d.getDate() + days); return d.toISOString().slice(0, 10)
}
function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

function StatusBadge({ status }: { status: AppointmentStatus }) {
  const { color, bg } = STATUS_COLORS[status]
  return (
    <span
      className="inline-flex items-center px-2 py-[0.15rem] rounded-full text-xs font-semibold"
      style={{ color, background: bg }}
    >
      {STATUS_LABELS[status]}
    </span>
  )
}

function TypeBadge({ type }: { type: AppointmentType }) {
  const { color, bg } = TYPE_COLORS[type]
  return (
    <span
      className="inline-flex items-center px-2 py-[0.15rem] rounded-full text-xs font-semibold"
      style={{ color, background: bg }}
    >
      {TYPE_LABELS[type]}
    </span>
  )
}

type ViewMode = 'calendar' | 'list'

function ViewToggle({ mode, onChange }: { mode: ViewMode; onChange: (m: ViewMode) => void }) {
  return (
    <div className="flex rounded-[7px] overflow-hidden border border-border">
      <button
        onClick={() => onChange('calendar')}
        className={`inline-flex items-center gap-[0.35rem] px-3 py-[0.35rem] text-[0.82rem] font-semibold border-none cursor-pointer border-r border-border ${mode === 'calendar' ? 'bg-accent text-accent-contrast' : 'bg-surface text-text-2'}`}
      >
        <Calendar size={14} /> Calendar
      </button>
      <button
        onClick={() => onChange('list')}
        className={`inline-flex items-center gap-[0.35rem] px-3 py-[0.35rem] text-[0.82rem] font-semibold border-none cursor-pointer ${mode === 'list' ? 'bg-accent text-accent-contrast' : 'bg-surface text-text-2'}`}
      >
        <List size={14} /> List
      </button>
    </div>
  )
}

export default function AppointmentList() {
  const [viewMode, setViewMode] = useState<ViewMode>('calendar')
  const [dateFrom, setDateFrom] = useState(todayISO())
  const [dateTo, setDateTo] = useState(plusDaysISO(7))
  const [statusFilter, setStatusFilter] = useState('')
  const [typeFilter, setTypeFilter] = useState('')

  const { data, loading, error } = useFetch(
    () => listAppointments({
      date_from: dateFrom ? `${dateFrom}T00:00:00Z` : undefined,
      date_to: dateTo ? `${dateTo}T23:59:59Z` : undefined,
      status: statusFilter || undefined,
      limit: 100,
    }),
    [dateFrom, dateTo, statusFilter],
  )

  const appointments: AppointmentListItem[] = (data ?? []).filter(
    a => !typeFilter || a.appointment_type === typeFilter,
  )

  const handleClearFilters = () => {
    setDateFrom(todayISO())
    setDateTo(plusDaysISO(7))
    setStatusFilter('')
    setTypeFilter('')
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <h1 className="m-0 text-[1.4rem] font-bold text-text-heading">Appointments</h1>
        <div className="flex items-center gap-3">
          <ViewToggle mode={viewMode} onChange={setViewMode} />
          {viewMode === 'list' && (
            <ButtonLink to="/appointments/new">+ New Appointment</ButtonLink>
          )}
        </div>
      </div>

      {viewMode === 'calendar' ? (
        <AppointmentCalendar />
      ) : (
        <>
          {/* List filters */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="flex items-center gap-[0.4rem]">
              <label className="text-[0.82rem] text-text-2 whitespace-nowrap">From</label>
              <input type="date" className={filterInputClass} value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
            </div>
            <div className="flex items-center gap-[0.4rem]">
              <label className="text-[0.82rem] text-text-2 whitespace-nowrap">To</label>
              <input type="date" className={filterInputClass} value={dateTo} onChange={e => setDateTo(e.target.value)} />
            </div>
            <select className={`${filterInputClass} cursor-pointer`} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="">All statuses</option>
              {(Object.keys(STATUS_LABELS) as AppointmentStatus[]).map(s => (
                <option key={s} value={s}>{STATUS_LABELS[s]}</option>
              ))}
            </select>
            <select className={`${filterInputClass} cursor-pointer`} value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
              <option value="">All types</option>
              {(Object.keys(TYPE_LABELS) as AppointmentType[]).map(t => (
                <option key={t} value={t}>{TYPE_LABELS[t]}</option>
              ))}
            </select>
            <Button variant="outline" size="sm" onClick={handleClearFilters}>Reset</Button>
          </div>

          {error && <ErrorBox className="mb-4">{error}</ErrorBox>}

          <Card className="overflow-hidden p-0">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr>
                    <th className={thClass}>Date &amp; Time</th>
                    <th className={thClass}>Patient</th>
                    <th className={thClass}>Client</th>
                    <th className={thClass}>Veterinarian</th>
                    <th className={thClass}>Type</th>
                    <th className={thClass}>Duration</th>
                    <th className={thClass}>Status</th>
                    <th className={`${thClass} text-right`}></th>
                  </tr>
                </thead>
                <tbody className={tbodyHoverClass}>
                  {loading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <tr key={i}>
                        {Array.from({ length: 8 }).map((_, j) => (
                          <td key={j} className={tdClass}>
                            <div className={`${skeletonClass} h-3.5 ${j === 7 ? 'w-12' : 'w-4/5'}`} />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : appointments.length === 0 ? (
                    <tr>
                      <td colSpan={8} className={tdClass}>
                        <div className="text-center py-12 px-4 text-text-3">
                          <Calendar size={36} strokeWidth={1.25} className="text-text-4 mb-2 mx-auto" />
                          <div className="font-semibold text-text-heading mb-[0.35rem]">No appointments found</div>
                          <div className="text-sm mb-4">
                            {statusFilter || dateFrom || dateTo ? 'Try adjusting the filters' : 'Schedule your first appointment to get started'}
                          </div>
                          <ButtonLink to="/appointments/new">+ New Appointment</ButtonLink>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    appointments.map((a) => (
                      <tr key={a.id}>
                        <td className={`${tdClass} whitespace-nowrap font-medium`}>{formatDateTime(a.scheduled_at)}</td>
                        <td className={tdClass}>
                          {a.patient_name ? (
                            <Link to={`/patients/${a.patient_id}`} className="text-accent no-underline font-semibold hover:underline">
                              {a.patient_name}
                            </Link>
                          ) : '—'}
                        </td>
                        <td className={`${tdClass} text-text-2`}>
                          {a.client_name ? (
                            <Link to={`/clients/${a.client_id}`} className="text-text-2 no-underline">
                              {a.client_name}
                            </Link>
                          ) : '—'}
                        </td>
                        <td className={`${tdClass} text-text-2`}>{a.veterinarian_name ?? '—'}</td>
                        <td className={tdClass}><TypeBadge type={a.appointment_type} /></td>
                        <td className={`${tdClass} text-text-2`}>{a.duration_minutes} min</td>
                        <td className={tdClass}><StatusBadge status={a.status} /></td>
                        <td className={`${tdClass} text-right whitespace-nowrap`}>
                          <Link to={`/appointments/${a.id}`} className="text-accent font-medium text-[0.82rem] no-underline">
                            View →
                          </Link>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  )
}
